import type { Chat } from "@codvero/shared";
import { Brain, Check, MessageSquare, X } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn.ts";
import { Input } from "@/ui/primitives.tsx";

/**
 * A Chat section conversation's memory: its own (the default), or another conversation's, shared by choice. Spaces
 * keep their own memory as well, whatever is chosen here.
 */
export function MemoryShareDialog({ chat, others, onPick, onClose }: { chat: Chat; others: Chat[]; onPick: (memoryFrom: string | null) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const list = others.filter((c) => !c.archived && (!needle || c.title.toLowerCase().includes(needle))).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const row = (id: string | null, label: string, hint: string | null, icon: typeof Brain) => {
    const Icon = icon;
    const on = (chat.memoryFrom ?? null) === id;
    return (
      <li key={id ?? "own"}>
        <button type="button" onClick={() => onPick(id)} className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start hover:bg-surface-2", on && "bg-accent-soft")}>
          <Icon className="size-4 shrink-0 text-fg-muted" />
          <span className="min-w-0 flex-1">
            <span dir="auto" className="block truncate text-sm">{label}</span>
            {hint && <span className="block text-[11px] text-fg-muted">{hint}</span>}
          </span>
          {on && <Check className="size-4 shrink-0 text-accent" />}
        </button>
      </li>
    );
  };
  return createPortal(
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/55 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="flex max-h-[80dvh] w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold">{t("memoryShare.title")}</h2>
            <p className="mt-1 text-xs text-fg-muted">{t("memoryShare.hint")}</p>
          </div>
          <button type="button" aria-label={t("common.close")} onClick={onClose} className="text-fg-muted hover:text-fg"><X className="size-5" /></button>
        </div>
        <ul>{row(null, t("memoryShare.own"), t("memoryShare.ownHint"), Brain)}</ul>
        <Input className="h-8 text-xs" placeholder={t("memoryShare.search")} value={q} onChange={(e) => setQ(e.target.value)} />
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {list.map((c) => row(c.id, c.title, null, MessageSquare))}
          {list.length === 0 && <li className="py-4 text-center text-xs text-fg-muted">{t("memoryShare.none")}</li>}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
